<script setup lang="ts">
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import RlDialog from '@/components/common/RlDialog.vue'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import { useSpareParts } from '@/composables/rc/spare-parts'
import type { ISparePartBulkUpdateForm } from '@/types/rc/forms'
import type { ISparePart } from '@/types/rc/spare-parts'
import { SparePartStatus } from '@/types/rc/spare-parts'
import { reduce } from 'lodash'
import { computed, reactive, ref, watchEffect } from 'vue'
import RcSiteSelect from '../common/RcSiteSelect.vue'
import RcSparePartCustomerSelect from '../common/RcSparePartCustomerSelect.vue'
import { SiteType } from '@/types/core/Site'

// Interface
const props = defineProps<{ spareParts: ISparePart[] }>()
const open = defineModel<boolean>()

// Buffers for selects

const selectedCustomer = ref<{ id: number; name: string }>()
const syncCustomerId = (customer: { id?: number } | undefined) => {
  bulkUpdateForm.customer_id = customer?.id
}

const selectedSite = ref<{ site_id?: string }>()
const syncSiteId = (site: { site_id?: string } | undefined) => {
  bulkUpdateForm.site_id = site?.site_id
}

// Form state

const anySelected = computed(() => Object.values(isActive).some((isSet) => isSet)) // At least one selected

const allSelectedAreSet = computed(() =>
  reduce(
    isActive,
    // A field is ok if it's inactive or active and has value set
    (acc, active, key) =>
      acc && (!active || (active && bulkUpdateForm[key as keyof ISparePartBulkUpdateForm]) != undefined),
    true
  )
)

// Actual form
const bulkUpdateForm = reactive<ISparePartBulkUpdateForm>({})

const isActiveInitialState = () => ({
  status: false,
  site_id: false,
  customer_id: false,
  price: false,
})

// Active elements meta-form
const isActive = reactive(isActiveInitialState())

// Reset forms
const onClosed = () => {
  Object.keys(bulkUpdateForm).forEach((k) => delete bulkUpdateForm[k as keyof typeof bulkUpdateForm])
  Object.assign(isActive, isActiveInitialState())
  isBulkUpdatePending.value = false
  selectedCustomer.value = undefined
  selectedSite.value = undefined
}

// BE call

const isBulkUpdatePending = ref(false)

const { bulkUpdateSparePartsMutation } = useSpareParts()

const { mutate, isError: isUpdateError, error: updateError, isPending: isUpdatePending } = bulkUpdateSparePartsMutation

const sparePartsIds = ref<number[]>([])
watchEffect(() => {
  sparePartsIds.value = props.spareParts.map((part) => part.id!)
})

const applyBulkSparePartUpdates = () => {
  isBulkUpdatePending.value = true
  mutate(
    { ids: sparePartsIds.value, form: bulkUpdateForm },
    {
      onSuccess: () => {
        open.value = false
      },
      onError: () => {
        isBulkUpdatePending.value = false
      },
    }
  )
}
</script>

<template>
  <RlDialog v-model="open" title="Bulk spare parts update" :width="544" @closed="onClosed">
    <div class="w-full p-6 flex flex-col gap-4">
      <p class="text-left">
        Make bulk changes to {{ spareParts.length }} selected spare part{{ spareParts.length == 1 ? '' : 's' }}
      </p>
      <div class="grid grid-cols-[15px_1fr_3fr] w-full pt-4 gap-4 items-center">
        <!---------------- Status ------------------>
        <el-checkbox v-model="isActive.status" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.status }]">Status</label>
        <el-select v-if="isActive.status" v-model="bulkUpdateForm.status" placeholder="Select status" class="w-full">
          <el-option v-for="option in Object.values(SparePartStatus)" :key="option" :label="option" :value="option" />
        </el-select>

        <!---------------- Owner ------------------>
        <el-checkbox v-model="isActive.customer_id" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.customer_id }]">Owner</label>
        <RcSparePartCustomerSelect
          v-if="isActive.customer_id"
          v-model="selectedCustomer"
          @update:model-value="syncCustomerId"
        />

        <!-------------- Location ---------------->
        <el-checkbox v-model="isActive.site_id" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.site_id }]">Location</label>
        <RcSiteSelect
          v-if="isActive.site_id"
          v-model="selectedSite"
          :type="SiteType.RepairCenter"
          @update:model-value="syncSiteId"
        />

        <!---------------- Price ----------------->
        <el-checkbox v-model="isActive.price" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.price }]">Price</label>
        <ElCurrencyInput v-if="isActive.price" v-model="bulkUpdateForm.price" />
      </div>
      <RlErrorAlert v-if="isUpdateError && !isUpdatePending" :error="updateError" />
    </div>
    <template #footer>
      <el-button size="large" @click="open = false">Cancel</el-button>
      <el-button
        size="large"
        type="primary"
        :disabled="!anySelected || !allSelectedAreSet"
        :loading="isBulkUpdatePending"
        @click="applyBulkSparePartUpdates"
        >Apply</el-button
      >
    </template>
  </RlDialog>
</template>
